import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch } from 'react-redux';

import CreditCardContent from './CreditCardContent';
import NurseryPayContent from './NurseryPayContent';

import type { PublicApiSettingsJson } from '@/domain/payment/service/entity/publicApiSettingsEntity';

import { paymentSlice } from '@/domain/payment/redux';
import { service } from '@/share';
import { paymentTypeNameMap } from '@/types/domain/photo/valueObjects/paymentType';

const PAYMENT_TYPES = ['none', 'nursery', 'creditCard'] as const;
type PaymentType = (typeof PAYMENT_TYPES)[number];
const paymentNameMap: { [key in PaymentType]: string } = {
  none: '支払い方法を選択してください',
  nursery: paymentTypeNameMap.nursery,
  creditCard: paymentTypeNameMap.creditCard,
};

/**
 * プロパティ
 */
export type Props = {
  /** 額 */
  amount: number,
  /** 現金で払う */
  payByCash: (amount: number) => void,
  /** カードで払う */
  payByCreditCard: (amount: number, cardId: string) => void,
  /** 閉じる */
  close: () => void,
  /** 施設ID */
  nurseryId: string,
}

const PaymentDialog: React.FC<Props> = ({
  amount,
  payByCash,
  payByCreditCard,
  close,
  nurseryId,
}) => {
  const dispatch = useDispatch();
  const [paymentType, setPaymentType] = React.useState<PaymentType>('none');
  const [cardId, setCardId] = React.useState<string | null>(null);
  const [id, setId] = React.useState<
    { data: PublicApiSettingsJson | undefined } | undefined
  >(undefined);
  React.useEffect(() => {
    if (id === undefined) {
      const execProcess = async () => {
        const data = await service.payment.getApiSettings(nurseryId);
        setId({ data });
      };
      execProcess();
    }
  }, [nurseryId, id]);
  const data = id?.data;
  if (data) {
    const comps = [];
    if (data.isEnableNurseryPayment) {
      let name = paymentNameMap.nursery;
      if (data.nurseryPaymentTitle && data.nurseryPaymentTitle !== '') {
        name += ` (${data.nurseryPaymentTitle})`;
      }
      comps.push(
        <MenuItem key="nursery" value="nursery">
          {name}
        </MenuItem>,
      );
    }
    if (
      data.squareApplicationId
      && data.squareLocationId
      && data.squareApplicationId !== ''
      && data.squareLocationId !== ''
    ) {
      comps.push(
        <MenuItem key="creditCard" value="creditCard">
          {paymentNameMap.creditCard}
        </MenuItem>,
      );
    }
    let disabled = true;
    if (comps.length > 0 && paymentType !== 'none') {
      if (paymentType === 'nursery') {
        disabled = false;
      } else if (['creditCard'].includes(paymentType)) {
        disabled = cardId === null;
      }
    }
    return (
      <Dialog open>
        <DialogTitle style={{ textAlign: 'center' }}>
          お支払い手続き
        </DialogTitle>
        {comps.length > 0 ? (
          <DialogContent>
            <h3>お支払い方法</h3>
            <Select
              value={paymentType}
              fullWidth
              onChange={(e) => setPaymentType(e.target.value as PaymentType)}
            >
              <MenuItem key="none" value="none">
                {paymentNameMap.none}
              </MenuItem>
              {comps}
            </Select>
            {paymentType === 'nursery' && (
              <NurseryPayContent />
            )}
            {paymentType === 'creditCard' && id && id.data && (
              <CreditCardContent
                id={id.data}
                nurseryId={nurseryId}
                cardId={cardId}
                setCardId={setCardId}
              />
            )}
            <p style={{ textAlign: 'center' }}>
              <Button
                onClick={() => dispatch(paymentSlice.actions.setSelectedNurseryId(nurseryId))}
              >
                ▶「特定商取引法に基づく表記」の確認はこちら
              </Button>
            </p>
            <p style={{ textAlign: 'center' }}>
              <span style={{ fontWeight: 'bold', fontSize: '1.0em' }}>
                お支払い金額（税込）：
              </span>
              <span style={{ fontWeight: 'bold', fontSize: '1.3em' }}>
                {`${amount.toLocaleString()} 円`}
              </span>
            </p>
          </DialogContent>
        ) : (
          <DialogContent>
            <p>施設の支払い設定がされていません。施設に問い合わせてください。</p>
          </DialogContent>
        )}
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={async () => {
              if (paymentType === 'nursery') {
                payByCash(amount);
              } else if (paymentType === 'creditCard') {
                payByCreditCard(amount, cardId!);
              }
              close();
            }}
            disabled={disabled}
            variant="contained"
            color="primary"
          >
            支払いを実行する
          </Button>
          <Button
            onClick={() => close()}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  return null;
};

export default PaymentDialog;
